import type { ComponentProps } from "react"
import { useEffect, useMemo, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Spinner from "@/v2-ui/spinner"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import { toast } from "@/v2-ui/toast"
import { errorToast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { useUptimeConfigQuery } from "@/v2-backoffice-client/uptime/config/uptime.config.query"
import { useUptimeEditPublicUpsertMutation } from "@/v2-backoffice-client/uptime/editPublic/uptime.editPublic.upsert.mutation"
import { useUptimeEditPublicDeleteMutation } from "@/v2-backoffice-client/uptime/editPublic/uptime.editPublic.delete.mutation"
import UptimeEditPublicForm from "@/v2-backoffice-client/uptime/editPublic/UptimeEditPublicForm"
import UptimeEditPublicPreview from "@/v2-backoffice-client/uptime/editPublic/UptimeEditPublicPreview"
import {
  derivePublicPreview,
  findOverride,
  hasFormChanges,
  makeFormState,
  type UptimeEditPublicFormState,
} from "@/v2-backoffice-client/uptime/editPublic/uptime.editPublic.utils"
import { getProviderName } from "@/v2-backoffice-client/uptime/uptime.utils"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"

export type UptimeEditPublicSectionProps = ComponentProps<"section"> & {
  target: UptimeTargetFragment
}

// UptimeEditPublicSection wraps the inline editor for the
// per-probe `status.TargetOverrides` row inside the uptime view
// sheet. The section is collapsed by default so engineers
// triaging an incident don't pay the visual cost; click "Edit
// public details" and the form + a side-by-side preview of what
// the public status page will render expand inline.
//
// Save semantics:
//   - Save writes one `upsertStatusTargetOverride` mutation that
//     atomically replaces the row.
//   - Reset to defaults issues a `deleteStatusTargetOverride`
//     mutation, falling back to the seed defaults the public
//     status API derives from category + provider lookups.
function UptimeEditPublicSection(props: UptimeEditPublicSectionProps) {
  const { target, className, ...sectionProps } = props
  const [isOpen, setIsOpen] = useState(false)

  // Long-cached config query; the section only needs Providers +
  // TargetOverrides but the same query feeds the Configuration
  // sheet so we share the cached payload.
  const {
    data,
    loading: configLoading,
    error: configError,
  } = useUptimeConfigQuery({
    skip: !isOpen,
  })

  const config = data?.getStatusConfig
  const override = useMemo(() => findOverride(config, target), [config, target])

  const [form, setForm] = useState<UptimeEditPublicFormState>(() =>
    makeFormState(null),
  )

  // Re-prime the form whenever the underlying override row changes
  // (first load, or after a save / reset round-trip refetches the
  // cached config). Comparing by row Id + UpdatedAt covers the
  // delete-then-insert case the upsert sproc takes when an
  // operator toggles every field back to default.
  useEffect(() => {
    setForm(makeFormState(override))
  }, [override?.Id, override?.UpdatedAt, override])

  const [upsert, { loading: saving, error: saveError }] =
    useUptimeEditPublicUpsertMutation({
      onCompleted() {
        toast({ title: "Public details saved" })
      },
    })
  const [removeOverride, { loading: resetting, error: resetError }] =
    useUptimeEditPublicDeleteMutation({
      onCompleted() {
        toast({ title: "Reverted to defaults" })
      },
    })

  errorToast(saveError ?? null, { title: "Failed to save public details" })
  errorToast(resetError ?? null, {
    title: "Failed to reset public details",
  })

  const isBusy = saving || resetting
  const isDirty = hasFormChanges(form, override)

  const preview = useMemo(
    () => derivePublicPreview(target, form, config),
    [target, form, config],
  )
  const defaultProviderName = useMemo(() => getProviderName(target), [target])

  if (!isOpen) {
    return (
      <section
        className={cn("uptime-edit-public-section", className)}
        {...sectionProps}
      >
        <div className="uptime-view-section-header">
          <div className="uptime-view-section-title">Public details</div>
          <div className="uptime-view-section-aside">
            customise what status.placepoint.no shows for this probe
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconSrc="pencil"
          className="uptime-edit-public-section-toggle"
          onClick={() => {
            setIsOpen(true)
          }}
        >
          Edit public details
        </Button>
        {override && (
          <p className="uptime-edit-public-section-hint">
            This probe has a saved override -- click to review or revert.
          </p>
        )}
      </section>
    )
  }

  return (
    <section
      className={cn("uptime-edit-public-section", className)}
      {...sectionProps}
    >
      <div className="uptime-view-section-header">
        <div className="uptime-view-section-title">Public details</div>
        <div className="uptime-view-section-aside">
          {override
            ? "Editing the saved override for this probe"
            : "No override yet -- saving will create one"}
        </div>
      </div>

      {configLoading && !config && (
        <Spinner displaySize="sm">Loading status configuration...</Spinner>
      )}
      {configError && <ErrorMessage error={configError} />}

      {config && (
        <div className="uptime-edit-public-section-body">
          <UptimeEditPublicForm
            value={form}
            providers={config.Providers}
            defaultDisplayName={target.Name ?? target.Identifier}
            defaultProviderName={defaultProviderName}
            isSaving={isBusy}
            onChange={setForm}
            onSubmit={(next) => {
              upsert({
                variables: {
                  input: {
                    source: target.Source,
                    identifier: target.Identifier,
                    displayName:
                      next.displayName.trim() === ""
                        ? null
                        : next.displayName.trim(),
                    description:
                      next.description.trim() === ""
                        ? null
                        : next.description.trim(),
                    isPublicHidden: next.isPublicHidden,
                    providerId: next.providerId === "" ? null : next.providerId,
                  },
                },
              })
            }}
          />

          <div className="uptime-edit-public-section-preview-wrap">
            <div className="uptime-edit-public-section-preview-label">
              Public preview
            </div>
            <UptimeEditPublicPreview
              displayName={preview.displayName}
              description={preview.description}
              providerName={preview.providerName}
              isHidden={preview.isHidden}
            />
          </div>

          <div className="uptime-edit-public-section-actions">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsOpen(false)
              }}
              disabled={isBusy}
            >
              Close
            </Button>
            {override && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                iconSrc="refresh"
                isLoading={resetting}
                disabled={isBusy}
                onClick={() => {
                  removeOverride({
                    variables: {
                      input: {
                        source: target.Source,
                        identifier: target.Identifier,
                      },
                    },
                  })
                }}
              >
                Reset to defaults
              </Button>
            )}
            <Button
              type="submit"
              size="sm"
              form="uptime-edit-public-form"
              isLoading={saving}
              disabled={!isDirty || isBusy}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}

export default UptimeEditPublicSection
